import React from "react";
import { Type, Star, Gauge, Hash, List, Smile, ToggleLeft, MapPin, MessageSquareText } from "lucide-react";
import { ContentPage, EligibilityCriterion, Fan, Player } from "@/services/bedrock";

export interface FormInfo {
  displayName: string;
  fanIdentityCollection: "hidden" | "optional" | "required";
  toneOfVoice: "professional" | "warm" | "casual" | "urgent";
  allowAiPersonalization: boolean;
  introTitle?: string;
  introText?: string;
  outroTitle?: string;
  outroText?: string;
  formFields: FormFieldInfo[];
}

export type FormFieldType =
  | "nps"
  | "ten_point"
  | "star"
  | "text"
  | "long_text"
  | "single_choice"
  | "boolean"
  | "sentiment"
  | "state";

export const FIELD_TYPE_MAP: Record<FormFieldType, { label: string; icon: React.ComponentType<any> }> = {
  text: { label: "Text", icon: Type },
  long_text: { label: "Paragraph", icon: MessageSquareText },
  star: { label: "Rating (Stars)", icon: Star },
  nps: { label: "NPS", icon: Gauge },
  ten_point: { label: "10-Point Score", icon: Hash },
  single_choice: { label: "Choice", icon: List },
  sentiment: { label: "Emojis", icon: Smile },
  boolean: { label: "Yes/No", icon: ToggleLeft },
  state: { label: "State", icon: MapPin },
};

export interface FormFieldInfo {
  fieldKey: string;
  title: string;
  subtitle?: string;
  fieldType: FormFieldType;
  required: boolean;
  position: number;
  fieldOptions?: {
    choices?: string[];
    collectionMode?: "first_party_only" | "third_party_only" | "hybrid";
    handoffTiming?: "on_completion" | "inline";
    internalTextPrompt?: string;
  };
}

export type LanguageCode = "en" | "es";

export interface Form extends FormInfo {
  id: number;
  active: boolean;
  contentPageThemeId: number | null;
  allowThemeChange: boolean;
  allowTranslations: boolean;
  supportedLanguages: LanguageCode[];
  translationStatus: "pending" | "completed" | "failed";
  translations?: Record<LanguageCode, any>;
  formFields: Array<FormField>;
  outroCtaLabel: string | null;
  outroCtaType: FormCtaType | null;
  outroCtaLink: string | null;
  testRequest: FormRequest | null;
  totalRequestCount?: number;
  totalResponseCount?: number;
  eligibilityCriterions?: EligibilityCriterion[];
}

export type FormCtaType =
  | "custom_link"
  | "custom_phone"
  | "custom_email"
  | "player_link"
  | "player_phone"
  | "player_email"
  | "share_player_link";

export interface FormField extends FormFieldInfo {
  id: number;
  active: boolean;
}

export interface FormRequest {
  id: number;
  formId: number;
  formDisplayName: string;
  shortCode: string;
  status: "pending_ai" | "ready" | "opened" | "completed";
  internalIdentifier: string | null;
  url: string;
  isTest: boolean;
  useAiPersonalization: boolean;
  contextInstructions: string | null;
  aiFieldOverrides: Record<string, any> | null;
  createdAt: string;
  updatedAt: string;
  form?: Form;
  player?: Player;
  fan?: Fan;
  totalResponseCount?: number;
  latestContentPage?: ContentPage;
}

export type FormResponseStatus = "initialized" | "in_progress" | "completed";

export interface FormResponse {
  id: number;
  isTest: boolean;
  status: FormResponseStatus;
  snapshotFieldKey: string | null;
  snapshotFieldValue: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  fan?: Fan;
  player?: Player;
  formFieldResponses: FormFieldResponse[];
}

export interface FormFieldResponse {
  id: number;
  value: any;
  createdAt: string;
  updatedAt: string;
  formField?: FormField;
}

export type FormDefaultType = "review" | "bad_review_feedback";

export interface FormDefault {
  id: number;
  formType: FormDefaultType;
  formId: number;
  coachId: number;
  segmentationDetailId: number;
  form: Form;
}
